import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import * as Belt from '@goodfellas/graduation'

import { LOCAL_STORAGE_API_KEY } from '../apis'
// =================================================================
// Competition History Store
// =================================================================
import {
  CompetitionMatch,
  CompetitionStats,
  Decision,
  MatchResult,
} from '../types/competition'
import { Fighter, FighterType } from '../types/fighter'

export interface User {
  phoneNumber: string | ''
  name: string | ''
  belt: string | ''
  degree: number | 0
  type: FighterType | ''
  step: string | ''
  group?: TableGroupType
  // Profile fields
  bio?: string
  instagram?: string
  avatar?: string | null
  achievements?: string[]
  _hasHydrated: boolean
  setUser: (user: User) => void
  setHasHydrated: (state: boolean) => void
}

const initialUserState = {
  phoneNumber: '' as const,
  name: '' as const,
  belt: '' as const,
  degree: 0,
  type: '' as const,
  step: '' as const,
  group: undefined as TableGroupType | undefined,
  bio: undefined as string | undefined,
  instagram: undefined as string | undefined,
  avatar: undefined as string | null | undefined,
  achievements: undefined as string[] | undefined,
  _hasHydrated: false,
}

export const useStore = create<User>()(
  persist(
    (set) => ({
      ...initialUserState,
      setUser: (user) =>
        set((state) => ({
          ...state,
          phoneNumber: user.phoneNumber,
          name: user.name,
          belt: user.belt,
          degree: user.degree,
          type: user.type,
          step: user.step,
          group: user.group,
          bio: user.bio,
          instagram: user.instagram,
          avatar: user.avatar,
          achievements: user.achievements,
        })),

      setHasHydrated: (hasHydrated: boolean) =>
        set({ _hasHydrated: hasHydrated }),
    }),
    {
      name: 'goodfellas:avaliacao360:user',
      storage:
        typeof window !== 'undefined'
          ? createJSONStorage(() => localStorage)
          : undefined,
      partialize: (state) => ({
        phoneNumber: state.phoneNumber,
        name: state.name,
        belt: state.belt,
        degree: state.degree,
        type: state.type,
        step: state.step,
        group: state.group,
        bio: state.bio,
        instagram: state.instagram,
        avatar: state.avatar,
        achievements: state.achievements,
      }),
      onRehydrateStorage: () => (state) => {
        // If there's no token, never allow persisted user state to bypass the login screen.
        if (typeof window === 'undefined') return
        if (!localStorage.getItem(LOCAL_STORAGE_API_KEY)) {
          state?.setUser({
            ...state,
            ...initialUserState,
            _hasHydrated: true,
          })
        }
        state?.setHasHydrated(true)
      },
    },
  ),
)

// Hook to check if store has been hydrated from localStorage
export const useHydration = () => {
  const hasHydrated = useStore((state) => state._hasHydrated)
  return hasHydrated
}

// =================================================================

// interface Fighter {
//   phoneNumber: string
//   name: string
//   belt: string
//   degree: number
//   type: string
// }

interface Group {
  _id: string
  groupName: string
  fighters: Fighter[]
}

export interface TableState {
  group: Group | null
  user: User | null
  setGroup: (group: Group) => void
  updateFighterData: (
    phoneNumber: string,
    name: string,
    belt: string,
    degree: number,
    type: string,
  ) => void
}

export type TableGroupType = { _id: string; group: string }

export const useTableGroupsStore = create<{
  groups: TableGroupType[]
  setGroups: (groups: TableGroupType[]) => void
}>((set) => ({
  groups: [],
  setGroups: (groups) => {
    set((state) => ({ ...state, groups }))
  },
}))

export const useTableStore = create<TableState>((set) => ({
  group: null,
  user: null,
  setGroup: (group) =>
    set((state) => ({
      ...state,
      group,
    })),
  updateFighterData: (phoneNumber, name, belt, degree, type) =>
    set((state) => {
      if (state.group) {
        const updatedFighters = state.group.fighters.map((fighter) => {
          if (fighter.phoneNumber === phoneNumber) {
            return {
              ...fighter,
              name,
              belt,
              degree,
              type,
            }
          }
          return fighter
        })
        return {
          group: {
            ...state.group,
            fighters: updatedFighters,
          },
        }
      }
      return state
    }),
}))

// =================================================================
// =================================================================
// =================================================================

interface Rating {
  fighterPhone: string
  fighterName: string
  fighterBelt: string
  fighterBeltDegree: number
  fighterType?: string
  pointsLeft: number
  gameStyle: number
  personalSkills: {
    technique: number
    strength: number
    agility: number
    stamina: number
    commitment: number
    focus: number
  }
  gameSkills: {
    guard: number
    guardRecovery: number
    guardPassing: number
    sweep: number
    takedown: number
    submission: number
    submissionDefense: number
    positionDefense: number
  }
  isRated: boolean
}

interface FeedbackActions {
  updateRating: (
    fighterPhone: string,
    category: string,
    rating: Partial<Rating>,
  ) => void
  addPoint: (fighter: string, skill: string) => void
  removePoint: (fighter: string, skill: string) => void
  assignStar: (fighter: string, skill: string, stars: number) => void
  moveSlider: (fighter: string, value: number) => void
  indexNext: () => void
  indexPrev: () => void
  indexSelect: (index: number) => void
  checkIfRated: (fighterPhone: string) => void
  setFeedback: (fetchedData: FeedbackState) => void
  setError: (message?: string | null) => void
  reset: () => void
}
export interface FeedbackState {
  phoneNumber: string
  date: string
  name: string
  belt: string
  degree?: number
  type?: string
  ratings: Rating[]
  index: number
  error?: string | null
  feedbackActions: FeedbackActions
}

export const useFeedbackStore = create<FeedbackState>((set, _get) => ({
  phoneNumber: '',
  date: '',
  name: '',
  belt: '',
  degree: 0,
  type: '',
  ratings: [],
  index: 0,
  error: null,
  feedbackActions: {
    reset: () =>
      set((state: FeedbackState) => ({
        ...state,
        ratings: [],
        index: 0,
        error: null,
      })),
    updateRating: (fighterPhone, category, updatedRating) =>
      set((state: FeedbackState) => {
        const ratings = state.ratings.map((rating) =>
          rating.fighterPhone === fighterPhone
            ? {
                ...rating,
                [category]: { ...rating[category], ...updatedRating },
              }
            : rating,
        )

        return { ratings }
      }),
    addPoint: (fighter: string, skill: string) =>
      // Find the fighter array through their name and add a point to the skill
      set((state: FeedbackState) => {
        const fighterIndex = state.ratings.findIndex(
          (rating) => rating.fighterPhone === fighter,
        )
        const updatedRating = { ...state.ratings[fighterIndex] }
        const updatedSkills = { ...updatedRating.gameSkills }

        // Mandatory statement to prevent the function from running if the fighter is not found
        if (fighterIndex === -1) {
          return state
        }

        // Prevent assigning points if there's no longer pointsLeft
        if (updatedRating.pointsLeft <= 0) {
          return state
        }

        // Prevent assigning more than 5 points to a skill
        if (updatedSkills[skill] >= 5) {
          return state
        }

        // Add a point to the skill
        updatedSkills[skill] += 1
        updatedRating.gameSkills = updatedSkills

        // Update the ratings array with the new skill points
        const updatedRatings = [...state.ratings]
        updatedRatings[fighterIndex] = updatedRating

        // Update the points left for the current fighter
        const updatedPointsLeft = updatedRating.pointsLeft - 1
        updatedRating.pointsLeft = updatedPointsLeft

        const personalSkills = Object.values(updatedRating.personalSkills)
        const isRated =
          personalSkills.some((skill) => skill !== 0) ||
          Object.values(updatedRating.gameSkills).some((skill) => skill !== 0)

        updatedRating.isRated = isRated

        return { ratings: updatedRatings }
      }),
    removePoint: (fighter: string, skill: string) =>
      // Find the fighter array through their name and remove a point from the skill
      set((state: FeedbackState) => {
        const fighterIndex = state.ratings.findIndex(
          (rating) => rating.fighterPhone === fighter,
        )
        const updatedRating = { ...state.ratings[fighterIndex] }
        const updatedSkills = { ...updatedRating.gameSkills }

        if (fighterIndex === -1) {
          return state // return current state if the fighter is not found
        }

        // Prevent assigning less than 0 points to a skill
        if (updatedSkills[skill] <= 0) {
          return state
        }

        updatedSkills[skill] -= 1

        updatedRating.gameSkills = updatedSkills

        const updatedRatings = [...state.ratings]
        updatedRatings[fighterIndex] = updatedRating

        // Update the points left for the current fighter
        const updatedPointsLeft = updatedRating.pointsLeft + 1
        updatedRating.pointsLeft = updatedPointsLeft

        return { ratings: updatedRatings }
      }),
    assignStar: (fighter: string, skill: string, stars: number) =>
      // Find the fighter array through their name and assign a number of stars to the personal skill
      set((state: FeedbackState) => {
        const fighterIndex = state.ratings.findIndex(
          (rating) => rating.fighterPhone === fighter,
        )

        if (fighterIndex === -1) {
          return state // return current state if the fighter is not found
        }

        const updatedRating = { ...state.ratings[fighterIndex] }
        const updatedSkills = { ...updatedRating.personalSkills }

        updatedSkills[skill] = stars

        updatedRating.personalSkills = updatedSkills

        const updatedRatings = [...state.ratings]
        updatedRatings[fighterIndex] = updatedRating

        const personalSkills = Object.values(updatedRating.personalSkills)
        const isRated =
          personalSkills.some((skill) => skill !== 0) ||
          Object.values(updatedRating.gameSkills).some((skill) => skill !== 0)

        updatedRating.isRated = isRated

        return { ratings: updatedRatings }
      }),
    moveSlider: (fighter: string, value: number) =>
      // Find the fighter array through their name and assign a value to the game style
      set((state: FeedbackState) => {
        const fighterIndex = state.ratings.findIndex(
          (rating) => rating.fighterPhone === fighter,
        )

        if (fighterIndex === -1) {
          return state // return current state if the fighter is not found
        }

        const updatedRating = { ...state.ratings[fighterIndex] }
        updatedRating.gameStyle = value

        const updatedRatings = [...state.ratings]
        updatedRatings[fighterIndex] = updatedRating

        return { ratings: updatedRatings }
      }),
    indexNext: () =>
      set((state: FeedbackState) => {
        const newIndex = state.index + 1
        return { index: newIndex }
      }),
    indexPrev: () =>
      set((state: FeedbackState) => {
        const newIndex = state.index - 1
        return { index: newIndex }
      }),
    indexSelect: (index: number) => set({ index }),
    checkIfRated: (fighterPhone: string) => {
      set((state) => {
        const rating = state.ratings.find(
          (r) => r.fighterPhone === fighterPhone,
        )
        if (!rating) return state

        const personalSkills = Object.values(rating.personalSkills)
        const gameSkills = Object.values(rating.gameSkills)

        const isRated =
          personalSkills.every((skill) => skill !== 0) ||
          gameSkills.every((skill) => skill !== 0)

        // Only update state when isRated actually changes to avoid infinite loop
        // (effect in fighterCards depends on store.ratings; updating always would re-trigger it)
        if (rating.isRated === isRated) return state

        return {
          ...state,
          ratings: state.ratings.map((r) =>
            r.fighterPhone === fighterPhone ? { ...r, isRated } : r,
          ),
        }
      })
    },
    setFeedback: (fetchedData: FeedbackState) => {
      set((state) => ({
        ...state,
        ...fetchedData,
        // Always clear transient errors on successful fetch
        error: null,
        // Keep actions stable
        feedbackActions: state.feedbackActions,
      }))
    },
    setError: (message?: string | null) => {
      set((state) => ({
        ...state,
        error: message ?? null,
      }))
    },
  },
}))

// =================================================================
// =================================================================
// =================================================================

export interface FighterCard {
  phoneNumber: string
  name: string
  belt: string
  degree: number
  type: string
  gameStyle: number
  personalSkills: {
    technique: number
    strength: number
    agility: number
    stamina: number
    commitment: number
    focus: number
  }
  gameSkills: {
    guard: number
    guardRecovery: number
    guardPassing: number
    sweep: number
    takedown: number
    submission: number
    submissionDefense: number
    positionDefense: number
  }
}

interface CardsActions {
  setCards: (fighters: FighterCard[]) => void
  setBeltAveragesAsBenchmark: () => void
  setSelectedFighter: (card: FighterCard | null) => void
  setSelectedComparisonFighter: (card: FighterCard | null) => void
  getFighterDataFromUserPhoneNumber: (
    phoneNumber: string,
  ) => FighterCard | undefined
  reset: () => void
}

interface GroupedCards {
  raw: FighterCard[]
  groupedByBelt: { [belt: string]: FighterCard[] }
  beltInOrder: string[]
}

export interface CardsState {
  cards: GroupedCards
  selectedFighter: FighterCard | null
  selectedComparisonFighter: FighterCard | null
  isStoreFilled: boolean
  cardsActions: CardsActions
}

export const useCardsStore = create<CardsState>((set, get) => ({
  cards: {
    raw: [],
    groupedByBelt: {},
    beltInOrder: [],
  },
  selectedFighter: null,
  selectedComparisonFighter: null,
  isStoreFilled: false,
  cardsActions: {
    reset: () =>
      set({
        cards: { raw: [], groupedByBelt: {}, beltInOrder: [] },
        selectedFighter: null,
        selectedComparisonFighter: null,
      }),
    setCards: (fighters) =>
      set((state) => {
        const beltOrder = [
          Belt.Colors.Red,
          Belt.Colors.RedWhite,
          Belt.Colors.RedBlack,
          Belt.Colors.Black,
          Belt.Colors.Brown,
          Belt.Colors.Purple,
          Belt.Colors.Blue,
          Belt.Colors.White,
        ]

        // Ordena as pessoas por faixa
        const groupedByBelt = fighters.reduce((acc, fighter) => {
          const belt = fighter.belt
          if (!acc[belt]) {
            acc[belt] = []
          }
          acc[belt].push(fighter)
          return acc
        }, {} as { [belt: string]: FighterCard[] })

        // Ordena as pessoas por ordem alfabética dentro das faixas
        Object.keys(groupedByBelt).forEach((belt) => {
          groupedByBelt[belt].sort((a, b) => a?.name?.localeCompare(b.name))
        })

        // Garante a ordem das faixas
        const beltsInOrder = beltOrder.filter((belt) => groupedByBelt[belt])

        const newState = {
          ...state,
          isStoreFilled: true,
          cards: {
            raw: fighters,
            groupedByBelt,
            beltInOrder: beltsInOrder,
          },
        }

        return newState
      }),
    setBeltAveragesAsBenchmark: () => {
      set((state) => {
        const { groupedByBelt, beltInOrder } = state.cards

        const beltAverages = Object.keys(groupedByBelt).map((belt) => {
          const fighters = groupedByBelt[belt]
          const totalFighters = fighters.length

          const personalSkillsAverage = {
            technique: 0,
            strength: 0,
            agility: 0,
            stamina: 0,
            commitment: 0,
            focus: 0,
          }

          const gameSkillsAverage = {
            guard: 0,
            guardRecovery: 0,
            guardPassing: 0,
            sweep: 0,
            takedown: 0,
            submission: 0,
            submissionDefense: 0,
            positionDefense: 0,
          }

          fighters.forEach((fighter) => {
            Object.keys(personalSkillsAverage).forEach((skill) => {
              personalSkillsAverage[skill] += fighter.personalSkills[skill]
            })

            Object.keys(gameSkillsAverage).forEach((skill) => {
              gameSkillsAverage[skill] += fighter.gameSkills[skill]
            })
          })

          Object.keys(personalSkillsAverage).forEach((skill) => {
            personalSkillsAverage[skill] /= totalFighters
          })

          Object.keys(gameSkillsAverage).forEach((skill) => {
            gameSkillsAverage[skill] /= totalFighters
          })

          const beltColor =
            Object.keys(Belt.Colors).find((key) => Belt.Colors[key] === belt) ||
            ''

          return {
            phoneNumber: '',
            name: `${beltColor}`,
            belt,
            degree: 0,
            type: '',
            gameStyle:
              fighters.reduce((acc, fighter) => acc + fighter.gameStyle, 0) /
              totalFighters,
            personalSkills: personalSkillsAverage,
            gameSkills: gameSkillsAverage,
          }
        })

        const benchmarkGroup = {
          benchmark: beltAverages,
        }

        const updatedBeltInOrder = ['benchmark', ...beltInOrder]

        return {
          cards: {
            ...state.cards,
            groupedByBelt: {
              ...benchmarkGroup,
              ...state.cards.groupedByBelt,
            },
            beltInOrder: updatedBeltInOrder,
          },
        }
      })
    },
    setSelectedFighter: (card) =>
      set(() => ({
        selectedFighter: card,
      })),
    setSelectedComparisonFighter: (card) =>
      set(() => ({
        selectedComparisonFighter: card,
      })),
    getFighterDataFromUserPhoneNumber: (phoneNumber: string) => {
      const state = get()
      const fighter = state.cards.raw.find(
        (person) => person.phoneNumber === `${phoneNumber}`,
      )
      return fighter
    },
  },
}))

// =================================================================

// =================================================================

interface CompetitionActions {
  setMatches: (matches: CompetitionMatch[]) => void
  addMatch: (match: CompetitionMatch) => void
  updateMatch: (matchId: string, match: Partial<CompetitionMatch>) => void
  removeMatch: (matchId: string) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  calculateStats: () => void
  reset: () => void
}

export interface CompetitionState {
  matches: CompetitionMatch[]
  stats: CompetitionStats
  loading: boolean
  error: string | null
  competitionActions: CompetitionActions
}

const initialStats: CompetitionStats = {
  totalMatches: 0,
  wins: 0,
  losses: 0,
  draws: 0,
  submissionWins: 0,
  submissionLosses: 0,
  winRate: 0,
}

export const useCompetitionStore = create<CompetitionState>((set, _get) => ({
  matches: [],
  stats: initialStats,
  loading: false,
  error: null,
  competitionActions: {
    setMatches: (matches) =>
      set((state) => {
        const stats = calculateCompetitionStats(matches)
        return {
          ...state,
          matches,
          stats,
        }
      }),
    addMatch: (match) =>
      set((state) => {
        const newMatches = [match, ...state.matches]
        const stats = calculateCompetitionStats(newMatches)
        return {
          ...state,
          matches: newMatches,
          stats,
        }
      }),
    updateMatch: (matchId, updatedMatch) =>
      set((state) => {
        const newMatches = state.matches.map((match) =>
          match.identificator.uuid === matchId
            ? { ...match, ...updatedMatch }
            : match,
        )
        const stats = calculateCompetitionStats(newMatches)
        return {
          ...state,
          matches: newMatches,
          stats,
        }
      }),
    removeMatch: (matchId) =>
      set((state) => {
        const newMatches = state.matches.filter(
          (match) => match.identificator.uuid !== matchId,
        )
        const stats = calculateCompetitionStats(newMatches)
        return {
          ...state,
          matches: newMatches,
          stats,
        }
      }),
    setLoading: (loading) => set({ loading }),
    setError: (error) => set({ error }),
    calculateStats: () =>
      set((state) => ({
        stats: calculateCompetitionStats(state.matches),
      })),
    reset: () =>
      set({
        matches: [],
        stats: initialStats,
        loading: false,
        error: null,
      }),
  },
}))

// Helper function to calculate stats
function calculateCompetitionStats(
  matches: CompetitionMatch[],
): CompetitionStats {
  const totalMatches = matches.length
  const wins = matches.filter((m) => m.result === MatchResult.WON).length
  const losses = matches.filter((m) => m.result === MatchResult.LOST).length
  const draws = matches.filter((m) => m.result === MatchResult.DRAW).length
  const submissionWins = matches.filter(
    (m) => m.result === MatchResult.WON && m.decision === Decision.SUBMISSION,
  ).length
  const submissionLosses = matches.filter(
    (m) => m.result === MatchResult.LOST && m.decision === Decision.SUBMISSION,
  ).length

  return {
    totalMatches,
    wins,
    losses,
    draws,
    submissionWins,
    submissionLosses,
    winRate: totalMatches > 0 ? (wins / totalMatches) * 100 : 0,
  }
}
